import { useState } from "react";
import { FileCheck, Plus } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDeleteFileByIdBo } from "@/api/file.api";
import { useGetAllFedexAccounts } from "@/api/fedexAccount.api";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { RemotePickupBadge } from "@/components/shared/badge/remotePickup.badge";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledCustomTextarea } from "@/components/ui/customs/controlled-custom-textarea";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { formatPrice, roundNumberToFixed } from "@/lib/money.util";
import { getExpeditionIdentifier } from "@/lib/expedition.util";
import { FinalizeExpeditionFormData } from "@/types/forms/bo/expeditionBo.form";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { ExpeditionDocumentCard } from "../../expeditionDocumentCard";
import { UploadFileBoModal } from "../../uploadFileModal";
import { DeliveryAddressBlock, ShipperAddressBlock } from "./addressBlocks";
import { FedexRateRecap } from "./fedexRateRecap";

const FilesToPrint = ({ expedition }: { expedition: GetOneExpeditionBoResponseDto }) => {
  const { t } = useTranslation("boExpeditionForm");
  const queryClient = useQueryClient();
  const { mutateAsync: deleteFile } = useDeleteFileByIdBo();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const filesToPrint = (expedition.expeditionDocuments?.documents ?? []).filter(
    (d) => d.type === FileTypeEnum.EXPEDITION_FILES_TO_PRINT,
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["expedition", "bo", expedition.id] });
  };

  const handleDelete = async (fileId: string) => {
    await deleteFile(fileId);
    invalidate();
    toast.success(t("filesToPrint.deleteSuccess"));
  };

  return (
    <>
      <div className="flex w-full flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-sm font-bold tracking-wider text-blue-600 uppercase">
          {t("filesToPrint.title")}
        </p>
        {filesToPrint.length === 0 ? (
          <p className="py-2 text-center text-sm text-gray-500">{t("filesToPrint.empty")}</p>
        ) : (
          filesToPrint.map((document) => (
            <ExpeditionDocumentCard key={document.id} file={document} onDelete={handleDelete} />
          ))
        )}
        <Button
          type="button"
          variant="ghost"
          className="h-auto self-start p-0 text-blue-600 no-underline hover:bg-transparent hover:text-blue-700"
          onClick={() => setUploadModalOpen(true)}
        >
          <Plus className="mr-1 size-4" />
          {t("filesToPrint.upload")}
        </Button>
      </div>
      <UploadFileBoModal
        expedition={expedition}
        open={uploadModalOpen}
        accept={["application/pdf"]}
        onOpenChange={setUploadModalOpen}
        expeditionDocumentsId={expedition.expeditionDocuments?.id}
        expeditionId={expedition.id}
        allowedFileTypes={[FileTypeEnum.EXPEDITION_FILES_TO_PRINT]}
        invalidateQueryKey={["expedition", "bo", expedition.id]}
        defaultOptions={{
          isPrivate: true,
          sendViaApi: false,
        }}
        disabledOptions={{
          isPrivate: true,
          sendViaApi: true,
        }}
        submitOnDrop
      />
    </>
  );
};

export const ExpeditionSummary = ({
  expedition,
}: {
  expedition: GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<FinalizeExpeditionFormData>();
  const { data: fedexAccounts } = useGetAllFedexAccounts();

  const remotePickup = expedition.expeditionOptions?.remotePickup ?? false;
  const dapOption = expedition.expeditionOptions?.dapOption ?? false;
  const shipperInfo = expedition.shipperInfo;
  const deliveryInfo = expedition.deliveryInfo;
  const fedexApi = expedition.expeditionFedexApi;
  const woApi = expedition.expeditionWoApi;
  const fedexRate = fedexApi?.fedexRate;
  const fedexAccount = fedexAccounts?.find((account) => account.id === fedexApi?.fedexAccountId);

  const fedexPackaging = fedexApi?.expeditionPackaging ?? [];

  const packageCount = fedexApi
    ? fedexPackaging.reduce((sum, pkg) => sum + pkg.count, 0)
    : woApi?.packageCount ?? 0;

  const totalWeight = fedexApi
    ? fedexPackaging.reduce((sum, pkg) => sum + pkg.weight * pkg.count, 0)
    : woApi?.weight ?? 0;

  const totalInsuredValue = fedexPackaging.reduce((sum, pkg) => sum + pkg.value * pkg.count, 0);

  const totalArticles = expedition.quotation
    ? expedition.quotation?.deliveryContent?.reduce(
        (sum, item) => sum + (item.numberOfBottles ?? 0),
        0,
      ) ?? 0
    : expedition.customQuotation?.quotationDeliveryContent?.reduce(
        (sum, item) => sum + (item.numberOfBottles ?? 0),
        0,
      ) ?? 0;

  const expeditionReference =
    fedexApi?.shippingReference || getExpeditionIdentifier(expedition) || "-";

  const deliveryInstructions = fedexApi?.deliveryInstructions?.trim();

  return (
    <AccordionItem
      value="expeditionSummary"
      className="shadow-basic flex w-full max-w-[1400px] flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-slate-100 p-6"
    >
      <AccordionTrigger className="flex w-full flex-row items-start justify-between gap-2 self-start !no-underline">
        <div className="flex flex-row items-center gap-2 self-start">
          <FileCheck className="h-6 w-6 !rotate-0 text-blue-600" />
          <p className="text-xl font-bold">{t("expeditionSummary.title")}</p>
        </div>
      </AccordionTrigger>
      <AccordionContent forceMount className="flex w-full flex-col gap-2">
        <div className="grid w-full grid-cols-1 gap-6 @3xl:grid-cols-2">
          <div className="flex w-full min-w-0 flex-col gap-6">
            <div className="flex w-full flex-col gap-1">
              <div className="flex flex-row items-center gap-2">
                <p className="text-sm font-bold tracking-wider text-blue-600 uppercase">
                  {t("expeditionSummary.pickupAddressTitle")}
                </p>
                {remotePickup && <RemotePickupBadge />}
              </div>
              <div className="flex flex-col gap-0.5 text-sm">
                <ShipperAddressBlock shipper={shipperInfo} />
              </div>
            </div>

            {(fedexApi || woApi) && (
              <div className="flex w-full flex-col gap-1">
                <p className="text-sm font-bold tracking-wider text-blue-600 uppercase">
                  {t("expeditionSummary.shipmentTitle")}
                </p>
                <div className="flex flex-col gap-0.5 text-sm">
                  <p className="font-medium text-gray-900">{expeditionReference}</p>
                  <p className="text-gray-900">
                    {t("expeditionSummary.packagesAndWeight", {
                      count: packageCount,
                      weight: roundNumberToFixed(totalWeight, 2),
                    })}
                  </p>
                  <p className="text-gray-900">
                    {t("expeditionSummary.articles", { count: totalArticles })}
                  </p>
                  {fedexApi && (
                    <p className="text-gray-900">
                      {t("expeditionSummary.insuredValue", {
                        value: formatPrice(totalInsuredValue),
                      })}
                    </p>
                  )}
                  <p className="text-gray-900">
                    {dapOption
                      ? t("expeditionSummary.taxesBilledToRecipient")
                      : t("expeditionSummary.taxesBilledToPelican")}
                  </p>
                </div>
              </div>
            )}

            {fedexRate && fedexApi?.fedexAccountId && (
              <FedexRateRecap
                rate={fedexRate}
                accountId={fedexApi.fedexAccountId}
                accountName={fedexAccount?.accountName ?? ""}
                accountNumber={fedexAccount?.accountNumber ?? ""}
              />
            )}
          </div>

          <div className="flex w-full min-w-0 flex-col gap-6">
            <div className="flex w-full flex-col gap-1">
              <p className="text-sm font-bold tracking-wider text-blue-600 uppercase">
                {t("expeditionSummary.deliveryAddressTitle")}
              </p>
              <div className="flex flex-col gap-0.5 text-sm">
                <DeliveryAddressBlock delivery={deliveryInfo} />
                {deliveryInstructions && (
                  <div className="h-fit w-full pt-2">
                    <p className="whitespace-pre-wrap text-gray-900">
                      <span className="font-bold">
                        {t("expeditionSummary.deliveryInstructionsLabel")}
                      </span>
                      {deliveryInstructions}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-0">
              <ControlledCustomTextarea
                label={t("expeditionSummary.packagingInstructionsLabel")}
                control={methods.control}
                name="packagingInstructions"
                className="w-full"
                rows={4}
              />
              <ControlledCheckbox
                control={methods.control}
                name="packagingPriority"
                label={t("expeditionSummary.packagingPriorityLabel")}
              />
              <FilesToPrint expedition={expedition} />
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
